import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { studentsApi } from '../services/api';
import { Camera, Save, Key, Shield, Eye, EyeOff } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const Profile: React.FC = () => {
  const { student, logout, updateStudent } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrData, setQrData] = useState<{qrCode: string, secret: string, manualEntryKey: string} | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [user2FAEnabled, setUser2FAEnabled] = useState(false);
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  const [disablePasswordValue, setDisablePasswordValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [stats, setStats] = useState({ completedCourses: 0, certificates: 0, studyHours: 0 });
  const [formData, setFormData] = useState({
    firstName: student?.firstName || '',
    lastName: student?.lastName || '',
    email: student?.email || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadStats();
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await studentsApi.getProfile();
      if (response.data) {
        setUser2FAEnabled(response.data.twoFactorEnabled || false);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadStats = async () => {
    try {
      const [coursesResponse, certificatesResponse] = await Promise.all([
        studentsApi.getEnrolledCourses(),
        studentsApi.getCertificates()
      ]);
      
      const completedCourses = coursesResponse.data?.courses?.filter((c: any) => c.progress === 100).length || 0;
      const certificates = certificatesResponse.data?.length || 0;
      const studyHours = Math.round((coursesResponse.data?.courses?.reduce((acc: number, c: any) => acc + (c.watchTime || 0), 0) || 0) / 3600);
      
      setStats({ completedCourses, certificates, studyHours });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await studentsApi.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      
      // Actualizar contexto con los nuevos datos
      if (response.data && updateStudent && student) {
        updateStudent({
          ...student,
          firstName: formData.firstName,
          lastName: formData.lastName
        });
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error al actualizar el perfil. Intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo y tamaño
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB.');
      return;
    }

    setAvatarUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await studentsApi.uploadAvatar(formData);
      
      if (response.data?.avatarUrl && updateStudent && student) {
        updateStudent({
          ...student,
          avatar: response.data.avatarUrl
        });
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Error al subir la imagen. Intenta nuevamente.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden.');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    
    setSaving(true);
    
    try {
      await studentsApi.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      toast.success('Contraseña actualizada exitosamente.');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Error al cambiar la contraseña. Verifica tu contraseña actual.');
    } finally {
      setSaving(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      const response = await studentsApi.enable2FA();
      
      if (response.data.success) {
        setQrData({
          qrCode: response.data.data.qrCode,
          secret: response.data.data.secret,
          manualEntryKey: response.data.data.manualEntryKey
        });
        setShow2FASetup(true);
        setShow2FAModal(false);
      }
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      toast.error('Error al generar código QR. Intenta nuevamente.');
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Ingresa un código de 6 dígitos');
      return;
    }

    try {
      const response = await studentsApi.verify2FA({ token: verificationCode });
      
      if (response.data.success) {
        setBackupCodes(response.data.data.backupCodes);
        setUser2FAEnabled(true);
        setShow2FASetup(false);
        toast.success('Autenticación 2FA configurada exitosamente');
        // Recargar perfil para sincronizar estado
        await loadUserProfile();
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast.error('Código inválido. Intenta nuevamente.');
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePasswordValue.trim()) {
      toast.error('Ingresa tu contraseña para desactivar 2FA');
      return;
    }

    try {
      const response = await studentsApi.disable2FA({ password: disablePasswordValue });
      
      if (response.data.success) {
        setUser2FAEnabled(false);
        setShowDisablePassword(false);
        setDisablePasswordValue('');
        toast.success('Autenticación 2FA desactivada exitosamente.');
        // Recargar perfil para sincronizar estado
        await loadUserProfile();
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error('Error al desactivar la autenticación 2FA. Verifica tu contraseña.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Mi Perfil</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Gestiona tu información personal y configuración</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información Personal */}
          <div className="cyber-container">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Información Personal</h2>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="cyber-button-secondary text-sm"
                >
                  Editar
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nombre
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    disabled={!isEditing}
                    className="cyber-input w-full"
                    placeholder="Tu nombre"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Apellido
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    disabled={!isEditing}
                    className="cyber-input w-full"
                    placeholder="Tu apellido"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  className="cyber-input w-full opacity-60"
                  placeholder="tu@email.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  El email no se puede modificar por seguridad
                </p>
              </div>

              {isEditing && (
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="cyber-button-primary flex items-center gap-2"
                  >
                    <Save size={16} />
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="cyber-button-secondary"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Seguridad */}
          <div className="cyber-container">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-6">Seguridad</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <Key size={20} className="text-cyan-500 dark:text-cyan-400" />
                  <div>
                    <p className="text-gray-800 dark:text-white font-medium">Cambiar Contraseña</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">Actualiza tu contraseña por seguridad</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowPasswordModal(true)}
                  className="cyber-button-secondary text-sm"
                >
                  Cambiar
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <Shield size={20} className={user2FAEnabled ? "text-green-500 dark:text-green-400" : "text-yellow-500 dark:text-yellow-400"} />
                  <div>
                    <p className="text-gray-800 dark:text-white font-medium">Autenticación en Dos Pasos</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{user2FAEnabled ? 'Activada - Tu cuenta está protegida' : 'Agrega una capa extra de seguridad'}</p>
                  </div>
                </div>
                <button 
                  onClick={user2FAEnabled ? () => setShowDisablePassword(true) : () => setShow2FAModal(true)}
                  className={`text-sm ${user2FAEnabled ? 'bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-400 px-3 py-1 rounded' : 'cyber-button-secondary'}`}
                >
                  {user2FAEnabled ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Avatar */}
          <div className="cyber-container text-center">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Foto de Perfil</h3>
            
            <div className="relative inline-block mb-4">
              <div className="w-24 h-24 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                {student?.avatar ? (
                  <img 
                    src={`https://api.escuela.agentesia.cloud${student.avatar}`} 
                    alt="Avatar" 
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-bold text-white">
                    {student?.firstName?.charAt(0)}{student?.lastName?.charAt(0)}
                  </span>
                )}
              </div>
              <label className={`absolute -bottom-2 -right-2 w-8 h-8 ${avatarUploading ? 'bg-gray-500' : 'bg-cyan-500 hover:bg-cyan-600'} rounded-full flex items-center justify-center cursor-pointer transition-colors`}>
                {avatarUploading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Camera size={16} className="text-white" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  disabled={avatarUploading}
                  className="hidden"
                />
              </label>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {avatarUploading ? 'Subiendo imagen...' : 'Haz clic en la cámara para cambiar tu foto'}
            </p>
          </div>

          {/* Stats rápidas */}
          <div className="cyber-container">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Mi Progreso</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Cursos completados</span>
                <span className="text-cyan-500 dark:text-cyan-400 font-medium">{stats.completedCourses}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Certificados obtenidos</span>
                <span className="text-cyan-500 dark:text-cyan-400 font-medium">{stats.certificates}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Horas de estudio</span>
                <span className="text-cyan-500 dark:text-cyan-400 font-medium">{stats.studyHours}h</span>
              </div>
            </div>
          </div>

          {/* Zona de peligro */}
          <div className="cyber-container border-red-500/30">
            <h3 className="text-lg font-semibold text-red-400 mb-4">Zona de Peligro</h3>
            <p className="text-gray-400 text-sm mb-4">
              Estas acciones son irreversibles. Úsalas con precaución.
            </p>
            <button
              onClick={logout}
              className="w-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-400 px-4 py-2 rounded-lg transition-colors text-sm"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Cambio de Contraseña */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Cambiar Contraseña</h3>
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contraseña Actual
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="cyber-input w-full"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="cyber-input w-full"
                  required
                  minLength={6}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirmar Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="cyber-input w-full"
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 cyber-button-primary flex items-center justify-center gap-2"
                >
                  <Key size={16} />
                  {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 cyber-button-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de 2FA */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Activar Autenticación 2FA</h3>
              <button 
                onClick={() => setShow2FAModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="text-center">
                <Shield size={48} className="text-green-400 mx-auto mb-4" />
                <p className="text-white font-medium mb-2">
                  Activar Autenticación en Dos Pasos
                </p>
                <p className="text-gray-400 text-sm mb-4">
                  Agrega una capa adicional de seguridad a tu cuenta usando una aplicación autenticadora como Google Authenticator o Authy.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleEnable2FA}
                  className="flex-1 cyber-button-primary flex items-center gap-2 justify-center"
                >
                  <Shield size={16} />
                  Continuar
                </button>
                <button
                  onClick={() => setShow2FAModal(false)}
                  className="flex-1 cyber-button-secondary"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Setup 2FA con QR */}
      {show2FASetup && qrData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Configurar Autenticador</h3>
              <button 
                onClick={() => setShow2FASetup(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-white font-medium mb-4">
                  Paso 1: Escanea este código QR con tu aplicación autenticadora:
                </p>
                <div className="bg-white p-4 rounded-lg inline-block">
                  <img src={qrData.qrCode} alt="QR Code" className="w-48 h-48" />
                </div>
              </div>
              
              <div className="bg-gray-800/50 p-4 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">
                  O ingresa manualmente esta clave:
                </p>
                <code className="text-cyan-400 text-sm font-mono break-all block">
                  {qrData.manualEntryKey}
                </code>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Paso 2: Ingresa el código de verificación (6 dígitos)
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="cyber-input w-full text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleVerify2FA}
                  disabled={verificationCode.length !== 6}
                  className="flex-1 cyber-button-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verificar y Activar
                </button>
                <button
                  onClick={() => setShow2FASetup(false)}
                  className="flex-1 cyber-button-secondary"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Códigos de Respaldo */}
      {backupCodes.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-md">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <Shield size={32} className="text-green-400" />
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">¡2FA Activado!</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Guarda estos códigos de respaldo en un lugar seguro. Podrás usarlos si pierdes acceso a tu authenticator:
                </p>
              </div>
              
              <div className="bg-gray-800/50 p-4 rounded-lg text-left max-h-40 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="font-mono text-sm text-cyan-400 p-2 bg-black/30 rounded text-center">
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="text-xs text-yellow-400 bg-yellow-500/10 p-3 rounded border border-yellow-500/20">
                ⚠️ Importante: Guarda estos códigos en un lugar seguro. No se mostrarán nuevamente.
              </div>
              
              <button
                onClick={() => {
                  setBackupCodes([]);
                  toast.success('Autenticación 2FA activada exitosamente. ¡Tu cuenta ahora está más segura!');
                }}
                className="w-full cyber-button-primary"
              >
                He guardado los códigos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para desactivar 2FA */}
      {showDisablePassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 rounded-2xl p-6 w-full max-w-md border border-red-500/30">
            <h3 className="text-xl font-bold text-white mb-4 text-center">
              Desactivar Autenticación 2FA
            </h3>
            
            <p className="text-gray-300 text-sm mb-6 text-center">
              Para tu seguridad, confirma tu contraseña para desactivar la autenticación en dos pasos.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contraseña actual
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={disablePasswordValue}
                    onChange={(e) => setDisablePasswordValue(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ingresa tu contraseña"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDisablePassword(false);
                    setDisablePasswordValue('');
                    setShowPassword(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-xl hover:bg-slate-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDisable2FA}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600"
                >
                  Desactivar 2FA
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default Profile;